import type { ServiceAuditTrailEntry } from "shared";

export type AuditSortDirection = "desc" | "asc";

/**
 * Orders audit entries by `created_at`.
 *
 * **Sorts entries, never the rendered rows.** One entry can produce several
 * sentences whose order carries meaning — reversing a flattened row list runs
 * those backwards inside the entry while the entries themselves reorder
 * correctly, which reads as scrambled text rather than a reordered timeline.
 *
 * We sort rather than reverse because the endpoint documents no order and takes
 * no sort parameter. Live responses do arrive `created_at DESC` — verified to
 * the millisecond — but a toggle that relies on undocumented behaviour is a
 * claim we can't keep. See `.claude/files/ticket-audit-trail-backend-api-requirements.md` §3.
 *
 * **Ties keep the API's order.** `Array.prototype.sort` is stable per spec, and
 * ties are not hypothetical: a summary and a description edited in one action
 * land on the same millisecond, and the API's own ordering of those is the only
 * signal we have about which came first.
 *
 * Returns a new array — the input is React Query's cached data, and sorting it
 * in place would leak this drawer's toggle into every other consumer.
 */
export const sortAuditEntries = (
  entries: ServiceAuditTrailEntry[],
  direction: AuditSortDirection,
): ServiceAuditTrailEntry[] =>
  [...entries].sort((a, b) => {
    const left = new Date(a.created_at).getTime();
    const right = new Date(b.created_at).getTime();
    // An unparseable date must not poison the comparator: NaN comparisons
    // return false in both directions, which degrades the whole sort to
    // "arbitrary" rather than misplacing the one bad entry. Treating the pair
    // as equal keeps every other entry correctly ordered around it.
    if (Number.isNaN(left) || Number.isNaN(right)) return 0;
    return direction === "desc" ? right - left : left - right;
  });
